'use client'

import { describeSprPrecisionMiss } from '@/lib/test-engine/spr-answer'

/**
 * The one-line explanation a student gets when their grid-in entry was the
 * right value rounded off too early — `4.33` for an answer of 13/3.
 *
 * Without it the feedback reads "Incorrect. The correct answer is 13/3." and
 * the student has no way to tell a formatting slip from a maths mistake: they
 * solved the question correctly and are told, in effect, that they didn't.
 *
 * Renders nothing unless `describeSprPrecisionMiss` recognises that exact
 * case, so an ordinary wrong answer never gets excused as a rounding problem.
 * Colour-free on purpose — it sits inside verdict boxes that are light on some
 * surfaces and theme-following on others.
 */
export default function SprPrecisionNote({
  entry,
  answerKey,
  className,
}: {
  /** What the student typed. */
  entry: string | null | undefined
  /** The stored answer key. */
  answerKey: string | null | undefined
  className?: string
}) {
  const miss = describeSprPrecisionMiss(entry, answerKey)
  if (!miss) return null
  const key = (answerKey ?? '').trim()
  const fraction = key.includes('/') ? key : null
  return (
    <p className={`mt-1.5 text-[13px] font-normal leading-relaxed ${className ?? ''}`}>
      You entered <strong>{(entry ?? '').trim()}</strong> — the right value, rounded off too early.
      A grid-in box holds 5 characters, so enter as many decimals as fit:{' '}
      <strong>{miss.suggestion}</strong>
      {fraction ? (
        <>
          {' '}
          (or just the fraction <strong>{fraction}</strong>)
        </>
      ) : null}
      .
    </p>
  )
}
